import { useEffect, useRef } from 'react';
import { Room } from 'twilio-video';

export function useReconnection({
  room,
  reconnect,
  onReconnectionFailed,
  accessToken,
}: {
  room: Room | null;
  reconnect: (token: string) => Promise<void>;
  onReconnectionFailed?: (error: Error) => void;
  accessToken: string | null;
}): void {
  const isReconnecting = useRef(false);

  useEffect(() => {
    const attemptReconnect = async () => {
      if (room && room.state !== 'disconnected') return;
      if (isReconnecting.current) return;

      isReconnecting.current = true;

      try {
        if (!accessToken) {
          throw new Error('Failed to get access token');
        }

        await reconnect(accessToken);
      } catch (error) {
        onReconnectionFailed?.(error as Error);
      } finally {
        isReconnecting.current = false;
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      attemptReconnect().catch(() => {});
    };

    const handleFocus = () => {
      attemptReconnect().catch(() => {});
    };

    const handleOnline = () => {
      attemptReconnect().catch(() => {});
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [room, reconnect, accessToken, onReconnectionFailed]);
}
